<template>
  <div class="viewer" @click.self="toCloseSlider">
    <ButtonClose class="viewer__close" @click="toCloseSlider"/>
    <ul class="viewer__list" ref="list" @click.self="toCloseSlider">
      <template v-for="(item, index) in DATA">
        <li class="viewer__item" v-if="item.type === 'video'" @click.self="toCloseSlider">
          <VideoSlide :ref="el => setVideoRef(el, index)" :data="item" />
        </li>
        <li class="viewer__item" v-else @click="toCloseSlider">
          <ImageSlide :ref="el => setImageRef(el, index)" :image="item" />
        </li>
      </template>
    </ul>
    <div class="viewer__dots" v-if="rawSlides.length > 1">
      <span
        v-for="(slide, index) in rawSlides"
        :key="index"
        class="viewer__dot"
        :class="{ 'viewer__dot--active': index + 1 === currentIndex }"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
//--------------------------------
import ImageSlide from '../../ui/ImageSlide/ImageSlide.vue';
import VideoSlide from '../../ui/VideoSlide/VideoSlide.vue';
import ButtonClose from '../../ui/ButtonClose/ButtonClose.vue';
import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
//--------------------------------
const store = useStore();
//--------------------------------
let isHandlingResize = false;
const rawSlides = [...store.state.Slider.slides];
const DATA = [rawSlides[rawSlides.length - 1], ...rawSlides, rawSlides[0]];
const list = ref(null);
const currentIndex = ref(store.state.Slider.startIndex + 1); 
const videoRefs = ref([]); 
const imageRefs = ref([]);
//--------------------------------
let currentOffsetSize = 0;
let transitioning = false; 
let startX = 0;
let currentX = 0;
let isDragging = false;
let sliderSwipeEnabled = true; // 🔹 управление свайпами
//--------------------------------
onMounted(async () => {
  await nextTick();
  defineOffsetSize();
  moveSlider(false);
  await new Promise(resolve => setTimeout(resolve, 200));
  addTransitionForSlider();
  addEventListeners();
  addTouchListeners();
  window.addEventListener('keydown', handleKeydown);
  if (list.value) {
    list.value.addEventListener('zoom-change', (e) => {
      const { zooming, edge } = e.detail
      sliderSwipeEnabled = !zooming || edge.left || edge.right
    })
  }
});
onBeforeUnmount(() => {
  removeEventListeners();
  removeTouchListeners();
  window.removeEventListener('keydown', handleKeydown); 
});
//--------------------------------
const setImageRef = (el, index) => {
  imageRefs.value[index] = el
}
const resetCurrentImageZoom = () => {
  const ref = imageRefs.value[currentIndex.value]
  if (ref && ref.resetZoom) ref.resetZoom()
}
//--------------------------------
const addEventListeners = () => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('wheel', onWheel, { passive: true });
};
const removeEventListeners = () => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('wheel', onWheel);
};
//--------------------------------
const addTouchListeners = () => {
  if (!list.value) return;
  list.value.addEventListener('touchstart', onTouchStart, { passive: true });
  list.value.addEventListener('touchmove', onTouchMove, { passive: true });
  list.value.addEventListener('touchend', onTouchEnd);
};
const removeTouchListeners = () => {
  if (!list.value) return;
  list.value.removeEventListener('touchstart', onTouchStart);
  list.value.removeEventListener('touchmove', onTouchMove);
  list.value.removeEventListener('touchend', onTouchEnd);
};
//--------------------------------
const moveSlider = async (withTransition = true) => {
  if (!list.value) return;
  const offset = currentOffsetSize * currentIndex.value;
  requestAnimationFrame(() => {
    if (!list.value) return;
    const items = list.value.querySelectorAll('.viewer__item');
    items.forEach(item => {
      item.style.transition = withTransition ? `transform .3s ease` : 'none';
      item.style.transform = `translateX(-${offset}px)`;
    });
  });
  await nextTick();
  updateVideoPlayback();
};
//--------------------------------
const defineOffsetSize = () => {
  if (!list.value) return;
  const item = list.value.querySelector('.viewer__item');
  if (item && item.offsetParent !== null) {
    currentOffsetSize = item.scrollWidth;
  }
};
//--------------------------------
const nextSlide = () => {
  if (transitioning) return;
  transitioning = true;
  resetCurrentImageZoom(); // 🔹 сброс зума
  currentIndex.value++;
  moveSlider(true);
  setTimeout(() => {
    if (currentIndex.value === DATA.length - 1) {
      currentIndex.value = 1;
      moveSlider(false);
    }
    transitioning = false;
  }, 350);
};
const prevSlide = () => {
  if (transitioning) return;
  transitioning = true;
  resetCurrentImageZoom(); // 🔹 сброс зума
  currentIndex.value--;
  moveSlider(true);
  setTimeout(() => {
    if (currentIndex.value === 0) {
      currentIndex.value = DATA.length - 2;
      moveSlider(false);
    }
    transitioning = false;
  }, 350);
};
//--------------------------------
const addTransitionForSlider = () => {
  if (!list.value) return;
  list.value.querySelectorAll('.viewer__item').forEach(item => {
    item.style.transition = `transform .3s ease`;
  });
};
//--------------------------------
const updateVideoPlayback = () => {
  const currentSlide = DATA[currentIndex.value];
  videoRefs.value.forEach((videoRef, idx) => {
    if (!videoRef || typeof videoRef.play !== 'function' || typeof videoRef.pause !== 'function') return;
    if (idx === currentIndex.value && currentSlide.type === 'video') {
      videoRef.play();
    } else {
      videoRef.pause();
    }
  });
};
const setVideoRef = (el, index) => {
  videoRefs.value[index] = el;
};
//--------------------------------
const handleResize = () => {
  if (isHandlingResize) return;
  isHandlingResize = true;
  requestAnimationFrame(() => {
    setTimeout(() => {
      defineOffsetSize();
      moveSlider(false);
      isHandlingResize = false;
    }, 0);
  });
};
//--------------------------------
const onWheel = (event) => {
  if (rawSlides.length < 2) return;
  if (transitioning) return;
  if (event.deltaY > 0) nextSlide();
  else if (event.deltaY < 0) prevSlide();
};
//--------------------------------
const onTouchStart = (e) => {
  if (!sliderSwipeEnabled) return; // 🔹 блокируем
  if (rawSlides.length < 2) return;
  if (transitioning) return;
  startX = e.touches[0].clientX;
  isDragging = true;
};
const onTouchMove = (e) => {
  if (!sliderSwipeEnabled) return; // 🔹 блокируем
  if (!isDragging || transitioning) return;
  currentX = e.touches[0].clientX;
  const deltaX = currentX - startX;
  if(!list.value) return;
  const items = list.value.querySelectorAll('.viewer__item');
  items.forEach(item => {
    item.style.transition = 'none';
    item.style.transform = `translateX(-${currentOffsetSize * currentIndex.value - deltaX}px)`;
  });
};
const onTouchEnd = () => {
  if (!sliderSwipeEnabled) return; // 🔹 блокируем
  if (!isDragging || transitioning) return;
  const deltaX = currentX - startX;
  const threshold = currentOffsetSize * 0.2;
  isDragging = false;

  if (deltaX > threshold) {
    prevSlide();
  } else if (deltaX < -threshold) {
    nextSlide();
  } else {
    moveSlider(true);
  }
  startX = 0;
  currentX = 0;
};
//--------------------------------
const toCloseSlider = () => store.dispatch('Dropdowns/removeTheDropdown', DROPDOWN_NAMES.slider);
const handleKeydown = (e) => {
  if (rawSlides.length < 2) return;
  if (transitioning) return;
  if (e.key === 'ArrowRight') {
    nextSlide();
  } else if (e.key === 'ArrowLeft') {
    prevSlide();
  }
};
</script>

<style lang="scss" scoped>
@import "./styles.scss";
</style>
